import React, { useEffect, useState } from 'react';
import { useNavigate, Link, useParams } from 'react-router-dom';
import {
  ChevronLeft,
  Book,
  Database,
  Library,
  CalendarDays,
  FlaskConical,
  AlertCircle,
  Clock,
  CheckCircle2,
  Circle,
  ArrowRight,
} from 'lucide-react';

// Study Design status line for the configuration landing (FECOA-1600/1601).
const DESIGN_STATUS_BANNER = {
  draft: { label: 'Draft', badge: 'badge-gray', text: 'The design has not been submitted for approval yet.', cls: 'border-gray-200 bg-gray-50 text-gray-700 dark:border-gray-700 dark:bg-gray-800/60 dark:text-gray-300', Icon: AlertCircle },
  in_review: { label: 'Pending Review', badge: 'badge-info', text: 'Locked from editing until an admin decides.', cls: 'border-info-500/25 bg-info-50 text-info-800 dark:bg-info-500/10 dark:text-info-400', Icon: Clock },
  denied: { label: 'Needs Revision', badge: 'badge-danger', text: 'Returned by the reviewer. Address the feedback and resubmit.', cls: 'border-danger-500/25 bg-danger-50 text-danger-700 dark:bg-danger-500/10 dark:text-danger-400', Icon: AlertCircle },
  approved: { label: 'Approved', badge: 'badge-success', text: 'Approved and ready to publish.', cls: 'border-success-500/25 bg-success-50 text-success-800 dark:bg-success-500/10 dark:text-success-400', Icon: CheckCircle2 },
  published: { label: 'Published', badge: 'badge-success', text: 'Published and read-only. Create an amendment to make changes.', cls: 'border-success-500/25 bg-success-50 text-success-800 dark:bg-success-500/10 dark:text-success-400', Icon: CheckCircle2 },
  superseded: { label: 'Superseded', badge: 'badge-gray', text: 'Superseded by a newer published version. Kept read-only as part of the study record.', cls: 'border-gray-200 bg-gray-50 text-gray-600 dark:border-gray-700 dark:bg-gray-800/60 dark:text-gray-400', Icon: Clock },
};

// The four readiness checks the server counts for a draft's completion
// (study start day, arms, schedules, assessments on a schedule), read from the
// same draft data so the checklist matches the drafts list.
const readinessOf = (draft) => {
  const data = draft?.data || {};
  const schedules = Array.isArray(data.schedules) ? data.schedules.filter((x) => x && typeof x === 'object') : [];
  const arms = Array.isArray(data.arms) ? data.arms : [];
  const startDay = schedules[0]?.studyStartDay;
  const surveyIds = new Set(
    schedules.flatMap((schedule) => (Array.isArray(schedule.standardSurveys) ? schedule.standardSurveys : []))
  );
  const armNames = arms.map((arm) => (typeof arm === 'string' ? arm : arm?.name)).filter(Boolean);
  const scheduleNames = schedules.map((schedule) => schedule.name).filter(Boolean);
  return [
    {
      key: 'start',
      label: 'Study start day',
      done: startDay !== undefined && startDay !== null,
      detail: startDay !== undefined && startDay !== null ? `Day ${startDay}` : 'Not set yet',
    },
    {
      key: 'arms',
      label: 'Arms',
      done: arms.length > 0,
      detail: arms.length ? armNames.join(', ') || `${arms.length} arms` : 'No arms yet',
    },
    {
      key: 'schedules',
      label: 'Schedules',
      done: schedules.length > 0,
      detail: schedules.length ? scheduleNames.join(', ') || `${schedules.length} schedules` : 'No schedules yet',
    },
    {
      key: 'surveys',
      label: 'Assessments on a schedule',
      done: surveyIds.size > 0,
      detail: surveyIds.size ? `${surveyIds.size} ${surveyIds.size === 1 ? 'assessment' : 'assessments'} scheduled` : 'None scheduled yet',
    },
  ];
};
import toast from 'react-hot-toast';
import studyBuilderService from '../../services/studyBuilderService';
import ApprovalSettingsControl from '../AssessmentBuilder/ApprovalSettingsControl';
import ReviewWorkflowActions from '../AssessmentBuilder/ReviewWorkflowActions';
import permissionsService from '../../services/permissionsService';
import { APPROVE_STUDY_PERMISSION } from '../../services/reviewWorkflowApi';
import PageHeader from '../Shared/PageHeader';
import { PageLoader } from '../ui';

const isInternalUser = () => {
  try {
    return !!JSON.parse(localStorage.getItem('user') || '{}')?.is_internal;
  } catch {
    return false;
  }
};

// Logged-in user id, used to scope review-workflow actions (submitter-only).
const getCurrentUserId = () => {
  try {
    const u = JSON.parse(localStorage.getItem('user') || '{}');
    return u?.id ?? u?.user_id ?? u?.pk ?? null;
  } catch {
    return null;
  }
};

const StudyBuilderOptions = () => {
  const navigate = useNavigate();
  const { studyName } = useParams();
  const [loading, setLoading] = useState(true);
  const [studyData, setStudyData] = useState(null);
  const [canApproveStudy, setCanApproveStudy] = useState(false);
  const currentUserId = getCurrentUserId();

  // Approvers get the Review action next to the checklist, as in the drafts list.
  useEffect(() => {
    let active = true;
    permissionsService
      .hasPermission(APPROVE_STUDY_PERMISSION)
      .then((ok) => { if (active) setCanApproveStudy(!!ok); })
      .catch(() => {});
    return () => { active = false; };
  }, []);

  // Reload the draft after a review action (submit, withdraw, approve...).
  const refreshDraft = async () => {
    try {
      const draft = await studyBuilderService.getStudyDraftByName(studyName);
      if (draft) setStudyData(draft);
    } catch (error) {
      console.error('Error refreshing study draft:', error);
    }
  };

  const handleReviewClick = (draft) => {
    navigate(`/studybuilder/review/${encodeURIComponent(draft.name)}`, {
      state: { reviewEntity: draft },
    });
  };

  // Ensure draft ID is set when this component mounts
  useEffect(() => {
    const fetchStudyDraft = async () => {
      setLoading(true);
      try {
        // First check if we already have the draft ID
        let draftId = localStorage.getItem('currentStudyDraftId');
        
        // If not, fetch it based on study name
        if (!draftId && studyName) {
          const draft = await studyBuilderService.getStudyDraftByName(studyName);
          if (draft && draft.id) {
            draftId = draft.id.toString();
            localStorage.setItem('currentStudyDraftId', draftId);
            setStudyData(draft);
            
            if (draft.study) {
              localStorage.setItem('currentStudyId', draft.study.toString());
            }
          }
        } else if (studyName) {
          // If we have the ID but not the study data, fetch the data
          const draft = await studyBuilderService.getStudyDraftByName(studyName);
          setStudyData(draft);
        }
      } catch (error) {
        console.error('Error fetching study draft:', error);
        toast.error('Failed to load study draft data');
      } finally {
        setLoading(false);
      }
    };
    
    fetchStudyDraft();
  }, [studyName]);

  const handleNavigateToSchedule = async () => {
    try {
      // Ensure we have the draft ID before navigating
      const draftId = localStorage.getItem('currentStudyDraftId');
      
      if (!draftId) {
        // If no draft ID, fetch it first
        const draft = await studyBuilderService.getStudyDraftByName(studyName);
        if (draft && draft.id) {
          localStorage.setItem('currentStudyDraftId', draft.id.toString());
          if (draft.study) {
            localStorage.setItem('currentStudyId', draft.study.toString());
          }
        } else {
          throw new Error('Could not find study draft');
        }
      }
      
      // Now navigate to schedule builder
      navigate(`/studybuilder/${studyName}/schedule`);
    } catch (error) {
      console.error('Error navigating to schedule builder:', error);
      toast.error('Failed to load study design page');
    }
  };

  const formatStudyName = (name) => {
    if (!name) return '';
    try {
      return decodeURIComponent(name);
    } catch (e) {
      return name;
    }
  };

  const reviewStatus = studyData?.review_status || 'draft';
  const statusMeta = DESIGN_STATUS_BANNER[reviewStatus] || DESIGN_STATUS_BANNER.draft;
  const StatusIcon = statusMeta.Icon;
  const checks = readinessOf(studyData);
  const doneCount = checks.filter((check) => check.done).length;
  const designComplete = doneCount === checks.length;
  const isEditable = reviewStatus === 'draft' || reviewStatus === 'denied';

  return (
    <div>
      <Link to="/studybuilder" className="back-link">
        <ChevronLeft className="h-4 w-4" />
        Back to Study Builder
      </Link>

      <PageHeader
        icon={FlaskConical}
        title={formatStudyName(studyName)}
        titleAccessory={studyData ? <span className={`badge badge-dot ${statusMeta.badge}`}>{statusMeta.label}</span> : null}
        subtitle="Set up the study's assessments and design, then submit the design for approval."
      >
        {/* Must be the study's external_id (UUID) — the studies API routes on
            external_id, so the numeric `study` PK 404s (the old bug here). */}
        {isInternalUser() && studyData?.study_external_id && (
          <ApprovalSettingsControl studyId={studyData.study_external_id} />
        )}
      </PageHeader>

      {loading ? (
        <PageLoader label="Loading study information" className="py-16" />
      ) : (
        <div className="space-y-6">
          {/* Study Design status (FECOA-1600/1601) */}
          <div className={`flex items-start gap-2.5 rounded-lg border px-4 py-3 text-sm ${statusMeta.cls}`}>
            <StatusIcon className="mt-0.5 h-4 w-4 flex-shrink-0" />
            <span>
              <span className="font-semibold">Study Design:</span> {statusMeta.text}
            </span>
          </div>

          <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)]">
            {/* Study design, with the same readiness checks the drafts list counts */}
            <section className="card flex flex-col overflow-hidden">
              <div className="card-band">
                <div>
                  <h2 className="card-band-title flex items-center gap-2">
                    <CalendarDays className="h-5 w-5" />
                    Study Design
                  </h2>
                  <p className="card-band-description">Define schedule, arms, stages, and other elements of your study structure.</p>
                </div>
              </div>
              <div className="flex flex-1 flex-col p-6">
                <div className="flex items-baseline justify-between gap-3">
                  <p className="stat-label">Ready to submit</p>
                  <span className="text-xs font-semibold tabular-nums text-gray-500 dark:text-gray-400">
                    {doneCount} of {checks.length}
                  </span>
                </div>
                <div className="mt-2 h-2 overflow-hidden rounded-full bg-gray-100 dark:bg-gray-700" aria-hidden="true">
                  <div
                    className="h-full rounded-full bg-primary-600 transition-all duration-700 ease-out dark:bg-primary-500"
                    style={{ width: `${(doneCount / checks.length) * 100}%` }}
                  />
                </div>
                <ul className="mt-4 divide-y divide-gray-100 dark:divide-gray-700">
                  {checks.map((check) => (
                    <li key={check.key} className="flex items-start gap-3 py-2.5">
                      {check.done ? (
                        <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary-600 dark:text-primary-400" aria-label="Done" />
                      ) : (
                        <Circle className="mt-0.5 h-4 w-4 flex-shrink-0 text-gray-300 dark:text-gray-600" aria-label="Not done" />
                      )}
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{check.label}</p>
                        <p className="truncate text-xs text-gray-500 dark:text-gray-400">{check.detail}</p>
                      </div>
                    </li>
                  ))}
                </ul>
                <div className="mt-auto pt-6">
                  <button onClick={handleNavigateToSchedule} className="btn btn-primary w-full">
                    <Database className="h-4 w-4" />
                    Configure Design
                  </button>
                  {studyData && (
                    <div className="mt-3 flex justify-center">
                      <ReviewWorkflowActions
                        entity={studyData}
                        entityType="study"
                        currentUserId={currentUserId}
                        isApprover={canApproveStudy}
                        onChanged={refreshDraft}
                        onReview={handleReviewClick}
                      />
                    </div>
                  )}
                  {isEditable && (
                    <p className="mt-2 flex items-center justify-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                      {designComplete ? (
                        <>
                          Everything is in place. Submit the design for review when you are ready.
                        </>
                      ) : (
                        <>
                          Next: {checks.find((check) => !check.done)?.label}
                          <ArrowRight className="h-3 w-3" />
                        </>
                      )}
                    </p>
                  )}
                </div>
              </div>
            </section>
            {/* Assessments the design puts on its schedules */}
            <section className="card flex flex-col overflow-hidden">
              <div className="card-band">
                <div>
                  <h2 className="card-band-title flex items-center gap-2">
                    <Library className="h-5 w-5" />
                    Assessments
                  </h2>
                  <p className="card-band-description">Build the questionnaires this study uses, or copy them from a library.</p>
                </div>
              </div>
              <div className="flex flex-1 flex-col p-6">
                <p className="text-sm leading-relaxed text-gray-600 dark:text-gray-300">
                  Assessments are built in the Assessment Builder. Once they are ready, add them to the study's schedules in the study design.
                </p>
                <div className="mt-auto pt-6">
                  <button
                    // Scope the builder to this study's assessments when the draft is
                    // linked to a study; otherwise fall back to the full list (FECOA-1826)
                    onClick={() => navigate(studyData?.study ? `/assessment-builder?study=${studyData.study}` : '/assessment-builder')}
                    className="btn btn-secondary w-full"
                  >
                    <Book className="h-4 w-4" />
                    Open Builder
                  </button>
                </div>
              </div>
            </section>

          </div>
        </div>
      )}
    </div>
  );
};

export default StudyBuilderOptions;